import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { DIFFICULTIES_INTERVALS } from '../../constants/difficulties'
import {
  renderHtml,
  getMaxLength,
  orderAnswerOptionsDescending,
  isAnswerCorrect,
} from '../../utils'

import GrileService from '../../services/grile-service.js'

import { socket } from '../../socket.js'
import swal from 'sweetalert'

import 'reactjs-popup/dist/index.css'
import '@liveblocks/react-comments/styles.css'
import './grile.css'

import Grid from '../../components/Grid'
import DashboardLayout from '../../layouts/LayoutContainers/DashboardLayout'
import useGetAllTags from '../../hooks/useGetAllTags'
import { useAuthContext } from '../../context'
import { useTourContext } from '../../context/TourContext'
import MeService from '../../services/me-service'
import { toast } from 'react-toastify'
import StatsBanner from './stats-banner'

const testing = process.env.REACT_APP_TESTING

const Grila = () => {
  const [solvingModes, setSolvingModes] = useState([
    {
      name: 'Învățare Inteligentă',
      description:
        'A.I.-ul nostru îți oferă grilele care iti aduc cel mai mare progres, pe baza celor deja rezolvate',
      checked: true,
    },
    {
      name: 'Corectare Greșeli',
      description:
        'Primești grilele pe care le-ai greșit cel mai des, pentru a le stăpâni complet',
      checked: false,
    },
    {
      name: 'Provocare Maximă',
      description:
        'Primești cele mai dificile grile, pregătite să-ți testeze limitele',
      checked: false,
    },
  ])

  const [grileTypes, setGrileTypes] = useState([
    {
      id: 'single',
      name: 'Cu răspuns unic',
      checked: true,
    },
    {
      id: 'multiple',
      name: 'Cu răspuns multiplu',
      checked: false,
    },
  ])

  const handleChangeGrileTypes = index => {
    if (grileTypes[index].id === 'single') {
      localStorage.setItem(
        'singleChoice',
        JSON.stringify(!grileTypes[index].checked),
      )
    }
    if (grileTypes[index].id === 'multiple') {
      localStorage.setItem(
        'multipleChoice',
        JSON.stringify(!grileTypes[index].checked),
      )
    }
    setGrileTypes(prevTypes =>
      prevTypes.map((type, i) =>
        i === index ? { ...type, checked: !type.checked } : type,
      ),
    )
  }

  const [height, setHeight] = useState(0)
  const [hasSubscription, setHasSubscription] = useState(undefined)
  const [endTrial, setEndTrial] = useState(false)
  const [difficultyFilter, setDifficultyFilter] = useState([
    {
      key: 'easy',
      label: 'Ușoare',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.EASY.minValue,
      maxValue: DIFFICULTIES_INTERVALS.EASY.maxValue,
    },
    {
      key: 'medium',
      label: 'Medii',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.MEDIUM.minValue,
      maxValue: DIFFICULTIES_INTERVALS.MEDIUM.maxValue,
    },
    {
      key: 'hard',
      label: 'Grele',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.HARD.minValue,
      maxValue: DIFFICULTIES_INTERVALS.HARD.maxValue,
    },
  ])
  const [started, setStarted] = useState(null)
  const [trialGrileCount, setTrialGrileCount] = useState(null)
  const [currentGrila, setCurrentGrila] = useState(undefined)
  const [currentGrilaId, setCurrentGrilaId] = useState(-1)
  const [answer, setAnswer] = useState(null)

  const [isSkipping, setIsSkipping] = useState(false)
  const [isGettingNextGrid, setIsGettingNextGrid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkbox, setCheckbox] = useState([false, false, false, false])

  const [isAnswerSelected, setIsAnswerSelected] = useState(false)
  const [hint, setHint] = useState(undefined)
  const [pagesAuto, setPagesAuto] = useState([])
  const [pagesManual, setPagesManual] = useState([])
  const [book, setBook] = useState(null)
  const [hasHint, setHasHint] = useState(false)
  const [filteredTags, setFilteredTags] = useState([])

  const [time, setTime] = useState(null)
  const timerInterval = useRef(undefined)
  const [isMultipleChoice, setIsMultipleChoice] = useState(false)

  const [isReported, setIsReported] = useState(false)
  const [rated, setRated] = useState(false)

  const [comments, setComments] = useState(undefined)
  const [grid, setGrid] = useState()

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [gridChapters, setGridChapters] = useState([])
  const [gridSubject, setGridSubject] = useState([])
  const [userChoice, setUserChoice] = useState(null)
  const { chapters, coursesWithChapters } = useGetAllTags()
  const { userDetails } = useAuthContext()
  const { handleNextTourStep } = useTourContext()

  const [isGridSaved, setIsGridSaved] = useState(false)

  const [books, setBooks] = useState([])

  const [gridsToday, setGridsToday] = useState(undefined)
  const [correctGridsToday, setCorrectGridsToday] = useState(undefined)

  const handleChangeSolvingMode = useCallback(
    index => {
      if (hasSubscription) {
        setSolvingModes(prevModes =>
          prevModes.map((mode, i) =>
            i === index
              ? { ...mode, checked: true }
              : { ...mode, checked: false },
          ),
        )

        localStorage.setItem('selectedSolvingMode', index)
      }
    },
    [hasSubscription],
  )

  const handleGetRemainingTimeString = useCallback(() => {
    let remainingTimeMinutes = 0
    let remainingTimeSeconds = 0

    if (time !== null) {
      remainingTimeMinutes = Math.floor(time['solving_time'] / 60)
      remainingTimeSeconds = time['solving_time'] % 60
    }

    if (testing) {
      remainingTimeMinutes = 59
      remainingTimeSeconds = 59
    }

    const hours = Math.floor(remainingTimeMinutes / 60)
    remainingTimeMinutes %= 60

    const hoursString = hours < 10 ? `0${hours}` : hours

    const minutesString =
      remainingTimeMinutes < 10
        ? `0${remainingTimeMinutes}`
        : remainingTimeMinutes
    const secondsString =
      remainingTimeSeconds < 10
        ? `0${remainingTimeSeconds}`
        : remainingTimeSeconds

    if (hours > 0) {
      return `${hoursString}:${minutesString}:${secondsString}`
    } else {
      return `${minutesString}:${secondsString}`
    }
  }, [time])

  const handleChangeSelected = useCallback(
    async choice => {
      if (isMultipleChoice) {
        let newChoiceString = undefined
        const currentChoiceIndexInAnswer = String(userChoice).indexOf(
          choice.toString(),
        )

        if (currentChoiceIndexInAnswer !== -1) {
          // the user unchecked the choice
          newChoiceString =
            String(userChoice).substring(0, currentChoiceIndexInAnswer) +
            String(userChoice).substring(currentChoiceIndexInAnswer + 1)
        } else {
          newChoiceString =
            userChoice !== null ? String(userChoice) + choice : String(choice)
        }

        const newChoice = orderAnswerOptionsDescending(newChoiceString)

        if (newChoice !== undefined && newChoice !== null) {
          setUserChoice(newChoice)
        } else {
          setUserChoice(null)
        }
      } else {
        setUserChoice(choice)
      }
    },
    [userChoice, isMultipleChoice],
  )

  const handleSelectDifficulty = useCallback(
    index => {
      const copy = [...difficultyFilter]
      // const isOnlyOneChecked =
      //   copy.filter(difficulty => difficulty.checked).length === 1

      // if (isOnlyOneChecked && copy[index].checked) {
      //   return
      // }

      copy[index].checked = !copy[index].checked
      setDifficultyFilter(copy)
    },
    [difficultyFilter],
  )

  const handleStartTimer = useCallback(() => {
    timerInterval.current = setInterval(() => {
      setTime(current => {
        return {
          grilaId: current['grilaId'],
          solving_time: current['solving_time'] + 1,
        }
      })
    }, 1000)
  }, [])

  const updateCommentsAfterAddingCommentCallback = useCallback(
    currentComments => {
      if (currentComments !== undefined) {
        setComments(currentComments.comments)
      }
    },
    [],
  )

  const [explainAnswers, setExplainAnswers] = useState(null)

  const stopTimer = () => {
    timerInterval.current = clearInterval(timerInterval.current)
  }

  const startSolving = useCallback((response, update_grila = false) => {
    if (response?.end_trial) {
      setEndTrial(true)
      return
    }

    if (response?.id === -1) {
      setStarted(false)
      setTrialGrileCount(response?.trial_grile_count)
      setAnswer(null)
      return
    }

    setGridChapters(response?.capitole)
    setGridSubject(response?.materie)
    setIsMultipleChoice(response?.multiple_choice)
    setRated(false)
    if (response?.comments) {
      setComments(response?.comments)
    }

    setStarted(true)

    let newTime = null
    if (parseInt(localStorage.getItem('grilaId')) === response?.id) {
      newTime = localStorage.getItem('solving_time')
    }
    if (newTime === null) {
      newTime = 0
    } else {
      newTime = parseInt(newTime)
    }

    if (update_grila || response?.solving_time > newTime) {
      newTime = response?.solving_time
      localStorage.setItem('solving_time', newTime)
      localStorage.setItem('grilaId', response?.id)
    }

    const grila = JSON.parse(response?.json)

    const newGrila = []
    for (let index = 0; index < grila.length; index++) {
      if (grila[index].type === 'matrix') {
        const matrixContainer = {
          type: 'matrix',
          content: [],
          maxSize: -1,
        }

        while (grila[index].type === 'matrix') {
          matrixContainer.content.push(grila[index].content)
          index += 1
        }

        matrixContainer.maxSize = getMaxLength(matrixContainer.content)
        newGrila.push(matrixContainer)

        if (index < grila.length) {
          newGrila.push(grila[index])
        }
      } else if (grila[index].type === 'text') {
        grila[index].content = renderHtml(grila[index].content)
        newGrila.push(grila[index])
      } else {
        newGrila.push(grila[index])
      }
    }

    if (response?.answered) {
      setIsSubmitted(true)
      stopTimer()
      setTime({
        grilaId: response?.id,
        solving_time: newTime,
        stopped: true,
      })

      setAnswer({
        correct_answer: response?.correct_answer,
        user: response?.choice,
        explain: response['explain'],
        solvingTimePercent: response?.solving_time_percent,
      })
    } else {
      setTime({
        grilaId: response?.id,
        solving_time: newTime,
        stopped: false,
      })
      setIsSubmitted(false)
      setAnswer(null)
    }

    setHint(undefined)
    setCurrentGrila(newGrila)
    setGrid(response)
    setHasHint(response?.hint)
    setIsGridSaved(response?.is_saved || false)
    setPagesAuto(response?.pages_auto)
    setPagesManual(response?.pages_manual)
    setIsMultipleChoice(response?.multiple_choice)
    setCurrentGrilaId(response?.id)

    if (response?.book_name && response?.book_page_offset) {
      setBook({
        book_name: response?.book_name,
        book_page_offset: response?.book_page_offset,
      })
    }

    setBooks(response?.books)
  }, [])

  const setTimeBackend = useCallback(async () => {
    if (time !== null && !time.stopped) {
      const newTime = localStorage.getItem('solving_time')
      const grilaId = localStorage.getItem('grilaId')
      if (newTime && grilaId) {
        socket.emit(
          'update_solving_time',
          localStorage.getItem('token'),
          newTime,
          grilaId,
          response => {
            if (
              response['update_grila'] === true &&
              grilaId === localStorage.getItem('grilaId')
            ) {
              if (response?.id !== -1) {
                setAnswer({
                  correct_answer: response?.correct_answer,
                  user: response?.choice,
                  explain: response?.explain,
                  solvingTimePercent: response?.solving_time_percent,
                })
                setUserChoice(response?.choice)
                if (response.explain_answers) {
                  setExplainAnswers(JSON.parse(response.explain_answers))
                } else {
                  setExplainAnswers(null)
                }
              }

              startSolving(response, true)
            }
          },
        )
      }
    }
  }, [startSolving, time])

  const handleSetTimeLocalStorage = useCallback(() => {
    localStorage.setItem('grilaId', time['grilaId'])
    localStorage.setItem('solving_time', time['solving_time'])
  }, [time])

  const handleSubmit = async () => {
    setIsSubmitted(true)
    const payload = { answer: userChoice }
    setIsSubmitting(true)

    stopTimer()
    await setTimeBackend()

    GrileService.submit(payload)
      .then(response => {
        setAnswer({
          correct_answer: response.correct_answer,
          user: answer,
          explain: response['explain'],
          solvingTimePercent: response?.solving_time_percent,
        })

        setGridsToday(current => current + 1)
        if (isAnswerCorrect(userChoice, parseInt(response.correct_answer))) {
          setCorrectGridsToday(current => current + 1)
        }

        if (response.explain_answers) {
          setExplainAnswers(JSON.parse(response.explain_answers))
        } else {
          setExplainAnswers(null)
        }

        const newTime = response.solving_time
        setTime({
          grilaId: response['grila_id'],
          solving_time: newTime,
          stopped: true,
        })
        localStorage.setItem('solving_time', newTime)
        localStorage.setItem('grilaId', response['grila_id'])

        setComments(response.comments)
      })
      .catch(res => {
        // TODO: Aici putem trimite analytics ca avem o eroare
        console.log(res)
      })
      .finally(() => {
        setIsSubmitting(false)
        if (!userDetails?.tutorial) handleNextTourStep()
      })

    handleSetTimeLocalStorage()
  }

  const handleStartNow = useCallback(
    async (skip = false) => {
      let filters = ''
      const chaptersForFilter = hasSubscription
        ? filteredTags
        : chapters?.map(chapter => chapter?.id)

      for (let index = 0; index < chaptersForFilter?.length; index++) {
        filters += `${chaptersForFilter[index]},`
      }

      setExplainAnswers(null)

      filters = filters?.slice(0, -1)
      const checkedDifficulties = difficultyFilter.filter(
        difficulty => difficulty.checked,
      )
      let response
      if (skip) {
        if (hasSubscription) {
          try {
            setIsSkipping(true)
            const solvingMode = solvingModes.findIndex(mode => mode.checked)
            if (checkedDifficulties.length === 3) {
              response = await GrileService.skip(
                filters,
                grileTypes,
                solvingMode,
              )
            } else if (checkedDifficulties.length === 2) {
              response = await GrileService.skip(
                filters,
                grileTypes,
                solvingMode,
                checkedDifficulties[0].minValue,
                checkedDifficulties[0].maxValue,
                checkedDifficulties[1].minValue,
                checkedDifficulties[1].maxValue,
              )
            } else if (checkedDifficulties.length === 1) {
              response = await GrileService.skip(
                filters,
                grileTypes,
                solvingMode,
                checkedDifficulties[0].minValue,
                checkedDifficulties[0].maxValue,
              )
            } else {
              response = await GrileService.skip(
                filters,
                grileTypes,
                solvingMode,
              )
            }
            if (!response.error) {
              setGridChapters(response?.capitole)
              setGridSubject(response?.materie)

              if (
                response.respected_solving_mode !== undefined &&
                response.respected_solving_mode === false
              ) {
                handleChangeSolvingMode(0)
              }
            } else {
              toast?.error(response?.error)
              handleChangeSolvingMode(0)
            }
          } catch (error) {
            toast?.error(
              'O eroare necunoscută s-a produs. Modul de rezolvare a fost schimbat automat. Daca eroarea persistă, te rugăm să ne contactezi.',
            )
            handleChangeSolvingMode(0)
          } finally {
            setIsSkipping(false)
          }
        }
      } else {
        try {
          setIsGettingNextGrid(true)

          const solvingMode = solvingModes.findIndex(mode => mode.checked)
          if (checkedDifficulties.length === 3) {
            response = await GrileService.newBest(
              filters,
              grileTypes,
              solvingMode,
            )
          } else if (checkedDifficulties.length === 2) {
            response = await GrileService.newBest(
              filters,
              grileTypes,
              solvingMode,
              checkedDifficulties[0].minValue,
              checkedDifficulties[0].maxValue,
              checkedDifficulties[1].minValue,
              checkedDifficulties[1].maxValue,
            )
          } else if (checkedDifficulties.length === 1) {
            response = await GrileService.newBest(
              filters,
              grileTypes,
              solvingMode,
              checkedDifficulties[0].minValue,
              checkedDifficulties[0].maxValue,
            )
          } else {
            response = await GrileService.newBest(
              filters,
              grileTypes,
              solvingMode,
            )
          }
          if (!response.error) {
            setGridChapters(response?.capitole)
            setGridSubject(response?.materie)
          }
        } catch (error) {
          handleChangeSolvingMode(0)
        } finally {
          setIsGettingNextGrid(false)
        }
      }

      if (response?.error) {
        toast?.error(response?.error)
        handleChangeSolvingMode(0)
      } else if (
        response.respected_solving_mode !== undefined &&
        response.respected_solving_mode == false
      ) {
        toast?.warn(
          'Nicio grilă rămasă din modul selectat. Modul de rezolvare a fost schimbat automat.',
        )
        handleChangeSolvingMode(0)
      }

      if (!response.error) {
        setUserChoice(null)
        setIsReported(false)
        setRated(false)
        setAnswer(null)
        setIsAnswerSelected(false)
        setHeight(0)
        startSolving(response)
      }
    },
    [
      chapters,
      filteredTags,
      difficultyFilter,
      hasSubscription,
      grileTypes,
      solvingModes,
      handleChangeSolvingMode,
      startSolving,
    ],
  )

  const handleUpdateGrid = useCallback(
    async (skip = false) => {
      setHeight(0)
      await handleStartNow(skip)
    },
    [handleStartNow],
  )

  const handleRate = useCallback(
    async value => {
      if (!rated) {
        const payload = { grilaId: currentGrilaId, type: 0, rating: value }
        await GrileService.rate(payload)

        swal({
          title: 'Feedback trimis',
          text: 'Mulțumim!',
          icon: 'success',
          timer: '2000',
          buttons: false,
        })

        setRated(true)
      }
    },
    [rated, currentGrilaId],
  )

  const handleTagsCheckboxChange = useCallback(
    (event, tagId) => {
      let newFilteredTags
      if (filteredTags?.includes(tagId)) {
        if (filteredTags.length > 1) {
          newFilteredTags = [...filteredTags]
          newFilteredTags.splice(newFilteredTags.indexOf(tagId), 1)
          setFilteredTags(newFilteredTags)
        }
      } else {
        newFilteredTags = [...filteredTags]
        newFilteredTags.push(tagId)
        setFilteredTags(newFilteredTags)
      }
      if (newFilteredTags?.length > 1) {
        localStorage.setItem('tagFilters', newFilteredTags.join(','))
      } else {
        localStorage.setItem('tagFilters', filteredTags[0]?.toString())
      }
    },

    [filteredTags, setFilteredTags],
  )

  const handleGetTags = useCallback(async () => {
    const response = await GrileService.getAllFilters()
    setHasSubscription(response['has_subscription'])
  }, [])

  const handleUserGetHint = useCallback(async () => {
    const response = await GrileService.getHint(localStorage.getItem('grilaId'))
    setHint(response.content)
  }, [])

  const difficulty = useMemo(() => {
    if (grid?.difficulty >= 0 && grid?.difficulty < 0.29) {
      return <span className="text-error">Grea</span>
    } else if (grid?.difficulty >= 0.29 && grid?.difficulty < 0.87) {
      return <span className="text-partially-correct">Medie</span>
    } else {
      return <span className="text-correct">Usoară</span>
    }
  }, [grid?.difficulty])

  const justStarted = useMemo(() => {
    return !currentGrila && (started === false || endTrial)
  }, [currentGrila, started, endTrial])

  useEffect(() => {
    if (time !== null && !time.stopped) {
      if (timerInterval.current === undefined) {
        handleStartTimer()
      }
      handleSetTimeLocalStorage()
      if ((time['solving_time'] + 1) % 2 === 0) {
        setTimeBackend()
      }
    }
  }, [time, handleSetTimeLocalStorage, handleStartTimer, setTimeBackend])

  useEffect(() => {
    window.addEventListener('beforeunload', setTimeBackend)
    return () => {
      window.removeEventListener('beforeunload', setTimeBackend)
      setTimeBackend()
    }
  }, [setTimeBackend])

  useEffect(() => {
    const localStorageSingleChoice = localStorage.getItem('singleChoice')
    const localStorageMultipleChoice = localStorage.getItem('multipleChoice')
    if (!localStorageSingleChoice) {
      localStorage.setItem('singleChoice', JSON.stringify(true))
    }
    if (!localStorageMultipleChoice) {
      localStorage.setItem('multipleChoice', JSON.stringify(true))
    }

    setGrileTypes([
      {
        id: 'single',
        name: 'Cu răspuns unic',
        checked: JSON.parse(localStorageSingleChoice),
      },
      {
        id: 'multiple',
        name: 'Cu răspuns multiplu',
        checked: JSON.parse(localStorageMultipleChoice),
      },
    ])
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    localStorage.getItem('singleChoice'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    localStorage.getItem('multipleChoice'),
  ])

  useEffect(() => {
    const localStorageDifficultyFilter = JSON.parse(
      localStorage.getItem('difficultyFilter'),
    )
    if (!localStorageDifficultyFilter) {
      localStorage.setItem('difficultyFilter', JSON.stringify(difficultyFilter))
    }
  }, [difficultyFilter, hasSubscription])

  useEffect(() => {
    let localStorageSolvingMode = localStorage.getItem('selectedSolvingMode')
    if (!localStorageSolvingMode || isNaN(localStorageSolvingMode)) {
      localStorageSolvingMode = 0
      localStorage.setItem('selectedSolvingMode', 0)
    }

    handleChangeSolvingMode(parseInt(localStorageSolvingMode))
  }, [handleChangeSolvingMode])

  useEffect(() => {
    GrileService.best().then(response => {
      setGridsToday(parseInt(response.grileToday))
      setCorrectGridsToday(parseInt(response.correctGrileToday))

      if (response?.id !== -1) {
        setAnswer({
          correct_answer: response?.correct_answer,
          user: response?.choice,
          explain: response?.explain,
          solvingTimePercent: response?.solving_time_percent,
        })
        setUserChoice(response?.choice)
        if (response.explain_answers) {
          setExplainAnswers(JSON.parse(response.explain_answers))
        } else {
          setExplainAnswers(null)
        }
      }

      startSolving(response)
    })
  }, [startSolving])

  useEffect(() => {
    const localStorageDifficultyFilter = JSON.parse(
      localStorage.getItem('difficultyFilter'),
    )
    if (hasSubscription) {
      setDifficultyFilter(localStorageDifficultyFilter)
    }
  }, [hasSubscription])

  useEffect(() => {
    handleGetTags()
  }, [handleGetTags])

  useEffect(() => {
    const localStorageChapters = localStorage.getItem('tagFilters')

    if (!localStorageChapters) {
      if (userDetails?.examTypes?.length > 0 && coursesWithChapters) {
        const chaptersIds = userDetails?.examTypes
          ?.flatMap(course => coursesWithChapters[course])
          .map(chapter => chapter?.id)

        localStorage.setItem('tagFilters', chaptersIds.join(','))
        setFilteredTags(chaptersIds)
      }
      const chaptersIds = chapters?.map(chapter => chapter?.id)
      setFilteredTags(chaptersIds)
      localStorage.setItem('tagFilters', chaptersIds.join(','))
    } else {
      const newFilteredTagsFromLocalStorage = localStorageChapters
        .split(',')
        .map(chapterId => parseInt(chapterId))
      setFilteredTags(newFilteredTagsFromLocalStorage)
    }
  }, [userDetails, coursesWithChapters, setFilteredTags, chapters])

  const isLoading = isSkipping || isGettingNextGrid

  const handleChangeChaptersByCourse = async (course, isSelected) => {
    try {
      const newFilteredTags = [...filteredTags]
      if (isSelected) {
        for (
          let index = 0;
          index < coursesWithChapters[course].length;
          index++
        ) {
          if (newFilteredTags.includes(coursesWithChapters[course][index].id)) {
            newFilteredTags.splice(
              newFilteredTags.indexOf(coursesWithChapters[course][index].id),
              1,
            )
          }
        }
      } else {
        for (
          let index = 0;
          index < coursesWithChapters[course].length;
          index++
        ) {
          if (
            !newFilteredTags.includes(coursesWithChapters[course][index].id)
          ) {
            newFilteredTags.push(coursesWithChapters[course][index].id)
          }
        }
      }
      const selectedCourses = Object.keys(coursesWithChapters).filter(key =>
        coursesWithChapters[key]
          .map(chapter => chapter.id)
          .every(value => newFilteredTags.includes(value)),
      )
      setFilteredTags(newFilteredTags)
      localStorage.setItem('tagFilters', newFilteredTags.join(','))
      await MeService.setExamTypes({ examTypes: selectedCourses })
    } catch (error) {
      console.error(error)
    }
  }

  const noteTextAreaRef = useRef(null)

  const handleSaveGrid = async () => {
    const note = noteTextAreaRef.current.value

    try {
      const payload = { grila_id: currentGrilaId, note: note }
      await GrileService.saveGrid(payload)
      setIsGridSaved(true)
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveSavedGrid = async () => {
    try {
      const payload = { grila_id: currentGrilaId }
      await GrileService.removeSavedGrid(payload)
      setIsGridSaved(false)
    } catch (error) {
      console.error(error)
    }
  }

  const closeSession = async () => {
    const response = await GrileService.closeBestSession()
    startSolving(response)
  }

  return (
    <>
      {started || hasSubscription !== undefined ? (
        <StatsBanner
          gridsToday={gridsToday}
          correctGrids={correctGridsToday}
          closeSession={closeSession}
          started={started}
        />
      ) : null}
      <DashboardLayout>
        <Grid
          noteTextAreaRef={noteTextAreaRef}
          isGridSaved={isGridSaved}
          handleSaveGrid={handleSaveGrid}
          handleRemoveSavedGrid={handleRemoveSavedGrid}
          pagesAuto={pagesAuto}
          pagesManual={pagesManual}
          book={book}
          books={books}
          hasHint={hasHint}
          source={grid?.source}
          handleChangeChaptersByCourse={handleChangeChaptersByCourse}
          gridId={grid?.id}
          isSubmittingGrid={isSubmitting}
          height={height}
          setHeight={setHeight}
          handleChangeAnswer={handleChangeSelected}
          userChoice={userChoice}
          handleStartNow={handleStartNow}
          currentGrila={currentGrila}
          started={started}
          trialGrileCount={trialGrileCount}
          endTrial={endTrial}
          isMultipleChoice={isMultipleChoice}
          isGettingNextGrid={isGettingNextGrid}
          answer={answer}
          isSubmitted={isSubmitted}
          difficulty={difficulty}
          gridTags={gridChapters}
          handleGetRemainingTimeString={handleGetRemainingTimeString}
          isLoading={isLoading}
          justStarted={justStarted}
          handleSubmit={handleSubmit}
          filteredTags={filteredTags}
          isAnswerSelected={isAnswerSelected}
          handleTagsCheckboxChange={handleTagsCheckboxChange}
          hasSubscription={hasSubscription}
          handleUpdateGrid={handleUpdateGrid}
          currentGridId={currentGrilaId}
          comments={comments}
          handleUserGetHint={handleUserGetHint}
          hint={hint}
          rated={rated}
          handleRate={handleRate}
          checkbox={checkbox}
          setCheckbox={setCheckbox}
          handleSelectDifficulty={handleSelectDifficulty}
          difficultyFilter={difficultyFilter}
          gridChapters={gridChapters}
          gridSubject={gridSubject}
          updateCommentsAfterAddingCommentCallback={
            updateCommentsAfterAddingCommentCallback
          }
          solvingModes={solvingModes}
          handleChangeSolvingMode={handleChangeSolvingMode}
          grileTypes={grileTypes}
          handleChangeGrileTypes={handleChangeGrileTypes}
          explainAnswers={explainAnswers}
        />
      </DashboardLayout>
    </>
  )
}
export default Grila
